import { Search, Filter, ChevronDown, X } from 'lucide-react'
import { clsx } from 'clsx'
import type { TargetAccountsFilters, Owner } from '../types'
import { ACCOUNT_STATUS_OPTIONS } from '../types'

interface AccountFiltersProps {
  filters: TargetAccountsFilters
  owners: Owner[]
  sectors: string[]
  showFilters: boolean
  onToggleFilters: () => void
  onFilterChange: <K extends keyof TargetAccountsFilters>(key: K, value: TargetAccountsFilters[K]) => void
  onClearFilters: () => void
  hasActiveFilters: boolean
}

export function AccountFilters({
  filters,
  owners,
  sectors,
  showFilters,
  onToggleFilters,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
}: AccountFiltersProps) {
  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 mb-4">
      {/* Search and Filter Toggle Row */}
      <div className="flex items-center gap-3">
        {/* Search Box */}
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            placeholder="Search accounts..."
            className="w-full pl-9 pr-8 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          {filters.search && (
            <button
              onClick={() => onFilterChange('search', '')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter Toggle */}
        <button
          onClick={onToggleFilters}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
            showFilters
              ? 'bg-primary text-white'
              : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-[var(--border-color)]'
          )}
        >
          <Filter size={16} />
          Filters
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-amber-500 rounded-full" />
          )}
          <ChevronDown size={14} className={clsx('transition-transform', showFilters && 'rotate-180')} />
        </button>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="px-3 py-2 text-sm text-red-500 hover:text-red-600 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="flex gap-4 mt-4 pt-4 border-t border-[var(--border-color)]">
          {/* Owner Filter */}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-medium text-[var(--text-muted)] uppercase">Owner</label>
            <select
              value={filters.owner}
              onChange={(e) => onFilterChange('owner', e.target.value)}
              className="px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Owners</option>
              {owners.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.first_name && owner.last_name
                    ? `${owner.first_name} ${owner.last_name}`
                    : owner.email?.split('@')[0] || 'Unknown'}
                </option>
              ))}
            </select>
          </div>

          {/* Sector Filter */}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-medium text-[var(--text-muted)] uppercase">Sector</label>
            <select
              value={filters.sector}
              onChange={(e) => onFilterChange('sector', e.target.value)}
              className="px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Sectors</option>
              {sectors.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>
              ))}
            </select>
          </div>

          {/* Overall Score Filter */}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-medium text-[var(--text-muted)] uppercase">Overall Score</label>
            <select
              value={filters.overallScore}
              onChange={(e) => onFilterChange('overallScore', e.target.value)}
              className="px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Scores</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-medium text-[var(--text-muted)] uppercase">Status</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className="px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Statuses</option>
              {ACCOUNT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  )
}
